import React, { useEffect, useState } from "react";
import { Plus, CalendarDays, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/Bits";
import { DIVISIONS } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", brand: "", event: "", division: DIVISIONS[0], owner: "" });

  const load = () => api.get("/calendar").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.date || !form.event) return toast.error("Date and event are required");
    try {
      await api.post("/calendar", form);
      toast.success("Event added");
      setOpen(false);
      setForm({ date: "", brand: "", event: "", division: DIVISIONS[0], owner: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/calendar/${id}`); load(); };

  const grouped = events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        title="Unified Agency Calendar"
        subtitle="Deadlines · shoots · milestones · reviews"
        actions={
          <button data-testid="add-event-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Event
          </button>
        }
      />
      <div className="p-8">
        {events.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No scheduled events" hint="Add deadlines and shoots to your calendar." />
        ) : (
          <div className="space-y-6">
            {Object.keys(grouped).sort().map((date) => (
              <div key={date}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-display text-lg font-bold">{date}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <div className="space-y-2">
                  {grouped[date].map((e) => (
                    <div key={e.id} className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-sm px-4 py-3 hover:border-white/20 transition-colors group">
                      <div className="flex items-center gap-4">
                        <span className="text-[10px] font-mono uppercase tracking-wider border border-white/10 bg-white/[0.03] text-zinc-400 px-2 py-0.5 rounded-sm">{(e.division || "").split(" ")[0] || "Event"}</span>
                        <div>
                          <div className="text-sm text-zinc-200">{e.event}</div>
                          <div className="text-xs text-zinc-500">{e.brand} · {e.owner}</div>
                        </div>
                      </div>
                      <button data-testid={`delete-event-${e.id}`} onClick={() => remove(e.id)} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Calendar Event</DialogTitle><DialogDescription className="sr-only">Add a deadline, shoot or milestone to the agency calendar.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <input data-testid="event-date-input" type="date" className={inputCls} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            <input data-testid="event-title-input" className={inputCls} placeholder="Event *" value={form.event} onChange={(e) => setForm((f) => ({ ...f, event: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Brand" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} />
              <input className={inputCls} placeholder="Owner" value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} />
            </div>
            <select className={inputCls} value={form.division} onChange={(e) => setForm((f) => ({ ...f, division: e.target.value }))}>
              {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <button data-testid="event-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">Add Event</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
